import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { productsApi } from '@wtf/shared/api/products'
import * as ProductTypes from '@wtf/shared'

/**
 * Hook to fetch a product by ID
 * @param id - Product ID
 * @param options - Optional query options
 * @param options.refetchOnMount - Whether to refetch on mount. Defaults to false since feeds cache products.
 */
export function useProduct(
    id: string,
    options?: { refetchOnMount?: boolean | 'always' }
) {
    return useQuery({
        queryKey: ['product', id],
        queryFn: () => productsApi.getProductById(id!),
        enabled: !!id,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: Infinity, // Never GC products - they are pre-cached by feeds and rarely change
        refetchOnMount: options?.refetchOnMount ?? false, // Default false - feeds cache products
        refetchOnWindowFocus: false, // Never refetch on window focus
    })
}

/**
 * Hook to create a new product
 */
export function useCreateProduct() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (data: ProductTypes.CreateProductRequest) =>
            productsApi.createProduct(data),
        onSuccess: (newProduct: ProductTypes.ProductDto) => {
            // Add the new product to the product cache
            queryClient.setQueryData(['product', newProduct.id], newProduct)
        },
    })
}

/**
 * Hook to update a product
 */
export function useUpdateProduct() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: ProductTypes.UpdateProductRequest
        }) => productsApi.updateProduct(id, data),
        onSuccess: (updatedProduct: ProductTypes.ProductDto) => {
            // Update the product in the cache
            queryClient.setQueryData(
                ['product', updatedProduct.id],
                updatedProduct
            )
        },
    })
}

/**
 * Hook to like a product
 */
export function useLikeProduct() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (id: string) => productsApi.likeProduct(id),
        onSuccess: (_: void, id: string) => {
            // Update the product in the cache directly
            queryClient.setQueryData(
                ['product', id],
                (oldData: ProductTypes.ProductDto | undefined) => {
                    if (!oldData) return oldData
                    return { ...oldData, isLiked: true }
                }
            )
        },
    })
}

/**
 * Hook to unlike a product
 */
export function useUnlikeProduct() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (id: string) => productsApi.unlikeProduct(id),
        onSuccess: (_: void, id: string) => {
            // Update the product in the cache directly
            queryClient.setQueryData(
                ['product', id],
                (oldData: ProductTypes.ProductDto | undefined) => {
                    if (!oldData) return oldData
                    return { ...oldData, isLiked: false }
                }
            )
        },
    })
}

/**
 * Hook to scrape product details from a URL before creation
 */
export function useScrapeProduct() {
    return useMutation<ProductTypes.ScrapedProductDto, Error, string>({
        mutationFn: (url: string) => productsApi.scrapeProduct(url),
    })
}

/**
 * Helper hook to load multiple products at once
 */
export function useProductQueries(productIds: string[]) {
    return productIds.map((id) => useProduct(id))
}
